import { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  UserPlus,
  MoreHorizontal,
  Mail,
  Trash2,
  Plus,
  Link2,
  ExternalLink,
} from 'lucide-react';
import Routes from '@/router/routes';
import {
  useOrganization,
  useOrganizationMembers,
  useOrganizationWorkspaces,
  useUpdateSubscription,
  useUpdateRaProvider,
  useInviteUser,
  useResendInvite,
  useRemoveMember,
  useUpdateOrganizationMemberRole,
} from '@/hooks/api/useOrganizations';
import { useCreateWorkspace } from '@/hooks/api/useWorkspaces';
import {
  useHubspotStatus,
  useOrganizationHubspotLink,
  useUpdateOrganizationHubspotLink,
} from '@/hooks/api/useBilling';
import { OrganizationMember } from '@/api/organizations';
import { CreateWorkspaceDialog } from '@/components/dialogs/CreateWorkspaceDialog';
import { CopyableId } from '@/components/CopyableId';
import { HubspotEntitySearchInput } from '@/components/HubspotEntitySearchInput';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InviteMemberDialog } from '@/components/dialogs/InviteMemberDialog';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { formatDate, formatDateTime } from '@/lib/date';

export default function SuperAdminSingleOrganization() {
  const { org_id } = useParams<{ org_id: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('overview');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [createWorkspaceDialogOpen, setCreateWorkspaceDialogOpen] = useState(false);
  const [hubspotDialogOpen, setHubspotDialogOpen] = useState(false);
  const [hubspotCompanyIdInput, setHubspotCompanyIdInput] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    variant?: 'default' | 'destructive';
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });

  // Stable filter references — inline objects cause infinite re-renders
  // because React Query treats each new object reference as a key change.
  // Matches the pattern applied in SuperAdminSingleWorkspace by #1423.
  const defaultFilters = useMemo(() => ({ page: 1, limit: 100 }), []);

  // Queries
  const {
    data: organization,
    isLoading: orgLoading,
    isError: orgError,
    refetch: refetchOrg,
  } = useOrganization(org_id || '');
  const { data: membersData, isLoading: membersLoading } = useOrganizationMembers(
    org_id || '',
    defaultFilters,
  );
  const { data: workspacesData, isLoading: workspacesLoading } = useOrganizationWorkspaces(
    org_id || '',
  );
  const { data: hubspotLink, isLoading: hubspotLinkLoading } = useOrganizationHubspotLink(
    org_id || '',
  );
  const { data: hubspotStatus } = useHubspotStatus();

  // Mutations
  const updateSubscription = useUpdateSubscription();
  const updateRaProvider = useUpdateRaProvider();
  const createWorkspace = useCreateWorkspace();
  const inviteUser = useInviteUser();
  const resendInvite = useResendInvite();
  const removeMember = useRemoveMember();
  const updateMemberRole = useUpdateOrganizationMemberRole();
  const updateHubspotLink = useUpdateOrganizationHubspotLink();

  const handleBack = () => {
    navigate(Routes.super_admin_dashboard_organizations.path);
  };

  const handleCreateWorkspace = (name: string, ownerEmail: string, organizationId: string) => {
    createWorkspace.mutate(
      { organizationId, payload: { name, ownerEmail } },
      {
        onSuccess: (ws) => {
          setCreateWorkspaceDialogOpen(false);
          navigate(`${Routes.super_admin_dashboard_workspaces.path}/${ws._id}`);
        },
      },
    );
  };

  const handleToggleSubscription = (subscribed: boolean) => {
    if (!organization) return;

    setConfirmDialog({
      open: true,
      title: `${subscribed ? 'Activate' : 'Deactivate'} Subscription`,
      description: `Are you sure you want to ${subscribed ? 'activate' : 'deactivate'} the subscription for ${organization.name}?`,
      onConfirm: () => {
        updateSubscription.mutate(
          { id: organization._id, payload: { subscribed } },
          { onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })) },
        );
      },
    });
  };

  const handleChangeRaProvider = (provider: 'ANTHROPIC' | 'AZURE_OPENAI') => {
    if (!organization) return;

    const providerLabel = provider === 'ANTHROPIC' ? 'Anthropic (Claude)' : 'Azure OpenAI';
    setConfirmDialog({
      open: true,
      title: 'Change Research Assistant Provider',
      description: `Are you sure you want to switch the Research Assistant model provider for ${organization.name} to ${providerLabel}? This affects AI analysis for this organization.`,
      onConfirm: () => {
        updateRaProvider.mutate(
          { id: organization._id, payload: { researchAssistantProvider: provider } },
          { onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })) },
        );
      },
    });
  };

  const handleInviteMember = (
    email: string,
    role: string,
    workspaceIds?: string[],
    workspaceRole?: 'CAN_EDIT' | 'CAN_VIEW',
  ) => {
    if (!org_id) return;
    inviteUser.mutate(
      { id: org_id, payload: { email, role, workspaceIds, workspaceRole } },
      {
        onSuccess: () => {
          setInviteDialogOpen(false);
        },
      },
    );
  };

  const handleResendInvite = (member: OrganizationMember) => {
    if (!org_id) return;
    resendInvite.mutate({ id: org_id, email: member.email });
  };

  const handleRemoveMember = (member: OrganizationMember) => {
    if (!org_id) return;
    setConfirmDialog({
      open: true,
      title: 'Remove Member',
      description: `Are you sure you want to remove ${member.name || member.email} from this organization?`,
      onConfirm: () => {
        removeMember.mutate(
          { id: org_id, userId: member._id },
          { onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })) },
        );
      },
      variant: 'destructive',
    });
  };

  const handleChangeRole = (member: OrganizationMember, newRole: string) => {
    if (!org_id) return;
    updateMemberRole.mutate({ id: org_id, userId: member._id, role: newRole });
  };

  const handleOpenHubspotDialog = () => {
    setHubspotCompanyIdInput(hubspotLink?.hubspotCompanyId || '');
    setHubspotDialogOpen(true);
  };

  const handleSaveHubspotLink = () => {
    if (!org_id || !hubspotCompanyIdInput.trim()) return;
    updateHubspotLink.mutate(
      { id: org_id, payload: { hubspotCompanyId: hubspotCompanyIdInput.trim() } },
      { onSuccess: () => setHubspotDialogOpen(false) },
    );
  };

  const handleUnlinkHubspot = () => {
    if (!org_id) return;
    setConfirmDialog({
      open: true,
      title: 'Unlink HubSpot Company',
      description: 'Are you sure you want to unlink this organization from its HubSpot company?',
      onConfirm: () => {
        updateHubspotLink.mutate(
          { id: org_id, payload: { hubspotCompanyId: null } },
          { onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })) },
        );
      },
      variant: 'destructive',
    });
  };

  if (orgLoading) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (orgError) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-red-600 mb-4">Failed to load data. Please try again.</p>
        <Button variant="outline" onClick={() => refetchOrg()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="flex flex-col gap-6">
        <Button variant="ghost" onClick={handleBack} className="w-fit">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Organizations
        </Button>
        <div className="bg-card rounded-lg shadow-sm border border-border p-8 text-center">
          <p className="text-red-600">Organization not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumbs
        items={[
          { label: 'Organizations', path: Routes.super_admin_dashboard_organizations.path },
          { label: organization.name },
        ]}
      />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={handleBack} size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-foreground">{organization.name}</h1>
              <Badge variant={organization.subscribed ? 'paid' : 'free'}>
                {organization.subscribed ? 'Paid' : 'Free'}
              </Badge>
              {organization.deletedAt && <Badge variant="deleted">Deleted</Badge>}
            </div>
            <p className="text-muted-foreground mt-1">
              Owner: {organization.owner?.name || organization.owner?.email || 'N/A'}
            </p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="workspaces">Workspaces</TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="mt-6">
          <div className="bg-card rounded-lg shadow-sm border border-border">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="text-lg font-semibold">Members</h2>
              <Button onClick={() => setInviteDialogOpen(true)} size="sm">
                <UserPlus className="h-4 w-4 mr-2" />
                Invite Member
              </Button>
            </div>

            {membersLoading && (
              <div className="p-4 space-y-3">
                {['s1', 's2', 's3'].map((k) => (
                  <Skeleton key={k} className="h-16 w-full" />
                ))}
              </div>
            )}
            {!membersLoading &&
            (!membersData || !membersData.data || membersData.data.length === 0) ? (
              <div className="p-8 text-center">
                <p className="text-muted-foreground">No members found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="w-[70px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {membersData?.data?.map((member) => (
                    <TableRow key={member._id}>
                      <TableCell className="font-medium">
                        {[member.name, member.last_name].filter(Boolean).join(' ') || 'N/A'}
                      </TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>
                        <span className="capitalize">{member.role}</span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            (
                              { active: 'active', pending: 'pending' } as Record<
                                string,
                                'active' | 'pending'
                              >
                            )[member.status] ?? 'default'
                          }
                        >
                          {member.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(member.joinedAt || member.invitedAt)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {member.status === 'pending' && (
                              <>
                                <DropdownMenuItem onClick={() => handleResendInvite(member)}>
                                  <Mail className="h-4 w-4 mr-2" />
                                  Resend Invite
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            {member.role !== 'OWNER' && (
                              <>
                                <DropdownMenuSub>
                                  <DropdownMenuSubTrigger>Change Role</DropdownMenuSubTrigger>
                                  <DropdownMenuSubContent>
                                    <DropdownMenuItem
                                      onClick={() => handleChangeRole(member, 'ADMIN')}
                                      disabled={member.role === 'ADMIN'}
                                    >
                                      Admin
                                      {member.role === 'ADMIN' && ' (current)'}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleChangeRole(member, 'EDITOR')}
                                      disabled={member.role === 'EDITOR'}
                                    >
                                      Editor
                                      {member.role === 'EDITOR' && ' (current)'}
                                    </DropdownMenuItem>
                                  </DropdownMenuSubContent>
                                </DropdownMenuSub>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            <DropdownMenuItem
                              onClick={() => handleRemoveMember(member)}
                              className="text-red-600"
                              disabled={member.role === 'OWNER'}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remove Member
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="workspaces" className="mt-6">
          <div className="bg-card rounded-lg shadow-sm border border-border">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="text-lg font-semibold">Workspaces</h2>
              <Button onClick={() => setCreateWorkspaceDialogOpen(true)} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Create Workspace
              </Button>
            </div>

            {workspacesLoading && (
              <div className="p-4 space-y-3">
                {['s1', 's2', 's3'].map((k) => (
                  <Skeleton key={k} className="h-16 w-full" />
                ))}
              </div>
            )}
            {!workspacesLoading &&
            (!workspacesData || !workspacesData.data || workspacesData.data.length === 0) ? (
              <div className="p-8 text-center">
                <p className="text-muted-foreground">No workspaces found</p>
              </div>
            ) : (
              <Accordion type="single" collapsible className="px-4">
                {workspacesData?.data?.map((workspace) => (
                  <AccordionItem key={workspace._id} value={workspace._id}>
                    <AccordionTrigger>
                      <div className="flex items-center justify-between flex-1 pr-4">
                        <Link
                          to={`${Routes.super_admin_dashboard_workspaces.path}/${workspace._id}`}
                          className="font-medium text-blue-600 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {workspace.name}
                        </Link>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>{workspace.projectCount || 0} projects</span>
                          <span>{workspace.memberCount || 0} members</span>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pl-4 pr-2 pb-2 space-y-2">
                        <div className="text-sm text-muted-foreground">
                          <span className="font-medium">Created:</span>{' '}
                          {formatDate(workspace.createdAt)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          <span className="font-medium">Projects:</span>{' '}
                          {workspace.projectCount || 0}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          <span className="font-medium">Members:</span> {workspace.memberCount || 0}
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </TabsContent>

        <TabsContent value="overview" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">Organization Stats</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Members</span>
                  <span className="font-semibold">{organization.memberCount || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Workspaces</span>
                  <span className="font-semibold">{organization.workspaceCount || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Projects</span>
                  <span className="font-semibold">{organization.projectCount || 0}</span>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">Settings</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Subscription</p>
                    <p className="text-sm text-muted-foreground">
                      {organization.subscribed ? 'Paid plan active' : 'Free plan'}
                    </p>
                  </div>
                  <Switch
                    checked={organization.subscribed}
                    onCheckedChange={handleToggleSubscription}
                    disabled={updateSubscription.isPending || !!organization.deletedAt}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Research Assistant model</p>
                    <p className="text-sm text-muted-foreground">
                      {(organization.researchAssistantProvider ?? 'ANTHROPIC') === 'ANTHROPIC'
                        ? 'Anthropic (Claude)'
                        : 'Azure OpenAI'}
                    </p>
                  </div>
                  <Select
                    value={organization.researchAssistantProvider ?? 'ANTHROPIC'}
                    onValueChange={(value) =>
                      handleChangeRaProvider(value as 'ANTHROPIC' | 'AZURE_OPENAI')
                    }
                    disabled={updateRaProvider.isPending || !!organization.deletedAt}
                  >
                    <SelectTrigger className="w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ANTHROPIC">Anthropic (Claude)</SelectItem>
                      <SelectItem value="AZURE_OPENAI">Azure OpenAI</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">Organization Details</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-muted-foreground text-sm">Created</span>
                  <p className="font-medium">{formatDateTime(organization.createdAt)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">Last Updated</span>
                  <p className="font-medium">{formatDateTime(organization.updatedAt)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">Owner</span>
                  <p className="font-medium">{organization.owner?.name || 'N/A'}</p>
                  <p className="text-sm text-muted-foreground">
                    {organization.owner?.email || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">Organization ID</h3>
              <div className="space-y-3">
                <CopyableId value={organization._id} label="MongoDB ID" />
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">HubSpot</h3>
                <Button variant="outline" size="sm" onClick={handleOpenHubspotDialog}>
                  <Link2 className="h-4 w-4 mr-2" />
                  {hubspotLink?.hubspotCompanyId ? 'Change' : 'Link'}
                </Button>
              </div>

              {hubspotLinkLoading && <Skeleton className="h-10 w-full" />}

              {!hubspotLinkLoading && !hubspotLink?.hubspotCompanyId && (
                <p className="text-sm text-muted-foreground">Not linked to a HubSpot company.</p>
              )}

              {!hubspotLinkLoading && hubspotLink?.hubspotCompanyId && (
                <div className="space-y-3">
                  <div>
                    <span className="text-muted-foreground text-sm">Company ID</span>
                    <p className="font-medium mt-1 font-mono text-sm break-all">
                      {hubspotStatus?.portalId ? (
                        <a
                          href={`https://app.hubspot.com/contacts/${hubspotStatus.portalId}/company/${hubspotLink.hubspotCompanyId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline inline-flex items-center gap-1"
                        >
                          {hubspotLink.hubspotCompanyId}
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      ) : (
                        hubspotLink.hubspotCompanyId
                      )}
                    </p>
                  </div>
                  {hubspotLink.hubspotLinkedAt && (
                    <div>
                      <span className="text-muted-foreground text-sm">Linked</span>
                      <p className="font-medium mt-1">
                        {formatDateTime(hubspotLink.hubspotLinkedAt)}
                      </p>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600"
                    onClick={handleUnlinkHubspot}
                    disabled={updateHubspotLink.isPending}
                  >
                    Unlink
                  </Button>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <InviteMemberDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        onInvite={handleInviteMember}
        loading={inviteUser.isPending}
        organizationId={org_id}
      />

      <CreateWorkspaceDialog
        open={createWorkspaceDialogOpen}
        onOpenChange={setCreateWorkspaceDialogOpen}
        onSubmit={handleCreateWorkspace}
        loading={createWorkspace.isPending}
        organizationId={org_id}
        organizationName={organization.name}
      />

      <Dialog open={hubspotDialogOpen} onOpenChange={setHubspotDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>
              {hubspotLink?.hubspotCompanyId ? 'Change' : 'Link'} HubSpot Company
            </DialogTitle>
            <DialogDescription>
              Link this organization to a HubSpot company for billing/CRM cross-reference.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {hubspotStatus?.configured && (
              <div className="space-y-2">
                <Label>Search HubSpot</Label>
                <HubspotEntitySearchInput
                  kind="company"
                  onSelect={(id) => setHubspotCompanyIdInput(id)}
                  disabled={updateHubspotLink.isPending}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="hubspot-company-id">HubSpot Company ID</Label>
              <Input
                id="hubspot-company-id"
                placeholder="e.g. 123456789"
                value={hubspotCompanyIdInput}
                onChange={(e) => setHubspotCompanyIdInput(e.target.value)}
                disabled={updateHubspotLink.isPending}
              />
              <p className="text-xs text-muted-foreground">
                {hubspotStatus?.configured
                  ? 'Selected from search above, or paste an id manually.'
                  : 'HubSpot lookups are not configured — paste the company id manually.'}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setHubspotDialogOpen(false)}
              disabled={updateHubspotLink.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveHubspotLink}
              disabled={!hubspotCompanyIdInput.trim() || updateHubspotLink.isPending}
            >
              {updateHubspotLink.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={confirmDialog.onConfirm}
        variant={confirmDialog.variant}
        loading={
          updateSubscription.isPending || removeMember.isPending || updateHubspotLink.isPending
        }
      />
    </div>
  );
}
